export type RouteLineCoordinate = readonly [number, number]

export interface RouteLineSmoothingOptions {
  /** Maximum lateral display error while removing high-frequency GTFS noise. */
  simplificationToleranceMetres?: number
  /** Maximum distance removed from either side of a corner. */
  maxCornerTrimMetres?: number
  /** Ignore direction changes smaller than this. */
  minimumTurnDegrees?: number
  /** Coordinates that must remain exact, such as topology junctions. */
  protectedCoordinateKeys?: ReadonlySet<string>
}

export const DEFAULT_SIMPLIFICATION_TOLERANCE_METRES = 14
export const DEFAULT_MAX_CORNER_TRIM_METRES = 72
export const DEFAULT_MINIMUM_TURN_DEGREES = 1.5
export const MIN_SEGMENT_METRES = 0.5
// MapLibre ultimately consumes a polyline, so even a mathematically smooth curve must be
// tessellated. Six-degree chords keep the joins imperceptible at close map zooms without
// multiplying long, nearly-straight portions of a route.
const CURVE_SAMPLE_RADIANS = 6 * Math.PI / 180

export function routeLineCoordinateKey(coordinate: RouteLineCoordinate): string {
  return `${coordinate[0]},${coordinate[1]}`
}

function localVectorMetres(
  from: RouteLineCoordinate,
  to: RouteLineCoordinate,
): [number, number] {
  const meanLatitude = ((from[1] + to[1]) / 2) * Math.PI / 180
  return [
    (to[0] - from[0]) * Math.cos(meanLatitude) * 111_320,
    (to[1] - from[1]) * 111_320,
  ]
}

function length(vector: [number, number]): number {
  return Math.hypot(vector[0], vector[1])
}

function interpolate(
  from: RouteLineCoordinate,
  to: RouteLineCoordinate,
  fraction: number,
): [number, number] {
  return [
    from[0] + (to[0] - from[0]) * fraction,
    from[1] + (to[1] - from[1]) * fraction,
  ]
}

function cubicBezier(
  start: RouteLineCoordinate,
  firstControl: RouteLineCoordinate,
  secondControl: RouteLineCoordinate,
  end: RouteLineCoordinate,
  t: number,
): [number, number] {
  const inverse = 1 - t
  return [
    inverse ** 3 * start[0]
      + 3 * inverse * inverse * t * firstControl[0]
      + 3 * inverse * t * t * secondControl[0]
      + t ** 3 * end[0],
    inverse ** 3 * start[1]
      + 3 * inverse * inverse * t * firstControl[1]
      + 3 * inverse * t * t * secondControl[1]
      + t ** 3 * end[1],
  ]
}

function pushDistinct(output: [number, number][], coordinate: RouteLineCoordinate): void {
  const previous = output[output.length - 1]
  if (!previous || previous[0] !== coordinate[0] || previous[1] !== coordinate[1]) {
    output.push([coordinate[0], coordinate[1]])
  }
}

function pointToSegmentDistance(
  point: readonly [number, number],
  start: readonly [number, number],
  end: readonly [number, number],
): number {
  const dx = end[0] - start[0]
  const dy = end[1] - start[1]
  const lengthSquared = dx * dx + dy * dy
  if (lengthSquared === 0) return Math.hypot(point[0] - start[0], point[1] - start[1])
  const fraction = Math.max(0, Math.min(1,
    ((point[0] - start[0]) * dx + (point[1] - start[1]) * dy) / lengthSquared,
  ))
  return Math.hypot(
    point[0] - (start[0] + fraction * dx),
    point[1] - (start[1] + fraction * dy),
  )
}

/** Ramer-Douglas-Peucker over one span whose endpoints must be retained. */
function simplifySpan(
  projected: readonly (readonly [number, number])[],
  start: number,
  end: number,
  toleranceMetres: number,
  retained: Uint8Array,
): void {
  retained[start] = 1
  retained[end] = 1
  const stack: Array<readonly [number, number]> = [[start, end]]
  while (stack.length > 0) {
    const [spanStart, spanEnd] = stack.pop()!
    let furthestIndex = -1
    let furthestDistance = toleranceMetres
    for (let index = spanStart + 1; index < spanEnd; index++) {
      const distance = pointToSegmentDistance(
        projected[index], projected[spanStart], projected[spanEnd],
      )
      if (distance > furthestDistance) {
        furthestDistance = distance
        furthestIndex = index
      }
    }
    if (furthestIndex < 0) continue
    retained[furthestIndex] = 1
    stack.push([spanStart, furthestIndex], [furthestIndex, spanEnd])
  }
}

function simplifyRouteLine(
  coordinates: readonly RouteLineCoordinate[],
  toleranceMetres: number,
  protectedCoordinateKeys?: ReadonlySet<string>,
): [number, number][] {
  const distinct: [number, number][] = []
  for (const coordinate of coordinates) pushDistinct(distinct, coordinate)
  if (distinct.length < 3 || toleranceMetres <= 0) return distinct

  const origin = distinct[0]
  const cosine = Math.cos(origin[1] * Math.PI / 180)
  const projected = distinct.map((coordinate) => [
    (coordinate[0] - origin[0]) * cosine * 111_320,
    (coordinate[1] - origin[1]) * 111_320,
  ] as const)
  const retained = new Uint8Array(distinct.length)
  const anchors = [0]
  if (protectedCoordinateKeys) {
    for (let index = 1; index < distinct.length - 1; index++) {
      if (protectedCoordinateKeys.has(routeLineCoordinateKey(distinct[index]))) anchors.push(index)
    }
  }
  anchors.push(distinct.length - 1)
  for (let index = 1; index < anchors.length; index++) {
    simplifySpan(projected, anchors[index - 1], anchors[index], toleranceMetres, retained)
  }
  return distinct.filter((_coordinate, index) => retained[index] === 1)
}

/**
 * Builds the canonical route geometry shared by line rendering and vehicle motion.
 * The upstream input is never mutated, and endpoints/protected topology coordinates
 * remain byte-for-byte exact. Consumers must reuse the returned coordinate array rather
 * than independently transforming the upstream GTFS line.
 */
export function canonicalRouteLineGeometry(
  coordinates: readonly RouteLineCoordinate[],
  options: RouteLineSmoothingOptions = {},
): [number, number][] {
  const simplified = simplifyRouteLine(
    coordinates,
    Math.max(0, options.simplificationToleranceMetres
      ?? DEFAULT_SIMPLIFICATION_TOLERANCE_METRES),
    options.protectedCoordinateKeys,
  )
  if (simplified.length < 3) return simplified

  const maximumTrim = Math.max(0, options.maxCornerTrimMetres ?? DEFAULT_MAX_CORNER_TRIM_METRES)
  const minimumTurnRadians = Math.max(
    0,
    options.minimumTurnDegrees ?? DEFAULT_MINIMUM_TURN_DEGREES,
  ) * Math.PI / 180
  const output: [number, number][] = [[simplified[0][0], simplified[0][1]]]

  for (let index = 1; index < simplified.length - 1; index++) {
    const previous = simplified[index - 1]
    const corner = simplified[index]
    const next = simplified[index + 1]
    if (options.protectedCoordinateKeys?.has(routeLineCoordinateKey(corner))) {
      pushDistinct(output, corner)
      continue
    }

    const incoming = localVectorMetres(previous, corner)
    const outgoing = localVectorMetres(corner, next)
    const incomingLength = length(incoming)
    const outgoingLength = length(outgoing)
    if (incomingLength < MIN_SEGMENT_METRES || outgoingLength < MIN_SEGMENT_METRES) {
      pushDistinct(output, corner)
      continue
    }

    const dot = Math.max(-1, Math.min(1,
      (incoming[0] * outgoing[0] + incoming[1] * outgoing[1])
        / (incomingLength * outgoingLength),
    ))
    const turn = Math.acos(dot)
    if (turn < minimumTurnRadians || maximumTrim === 0) {
      pushDistinct(output, corner)
      continue
    }

    // At most half of either adjacent segment: neighboring corner curves can meet,
    // but cannot cross or reverse their order along the original line.
    const trimMetres = Math.min(maximumTrim, incomingLength / 2, outgoingLength / 2)
    const before = interpolate(previous, corner, 1 - trimMetres / incomingLength)
    const after = interpolate(corner, next, trimMetres / outgoingLength)
    pushDistinct(output, before)

    // Approximate the circular fillet tangent to both adjacent segments with a cubic
    // Bezier. Unlike using the corner itself as one quadratic control point, this keeps
    // curvature much more even through the bend while remaining C1-continuous with the
    // straight portions on both sides. For a turn angle theta, the fillet radius is
    // trim/tan(theta/2), and 4/3*r*tan(theta/4) is the standard cubic arc handle length.
    const radius = trimMetres / Math.tan(turn / 2)
    const handleMetres = 4 / 3 * radius * Math.tan(turn / 4)
    const firstControl = interpolate(before, corner, handleMetres / trimMetres)
    const secondControl = interpolate(after, corner, handleMetres / trimMetres)

    // Sample according to curvature, not physical length. A long, shallow bend needs
    // only two chords; tying density to trim distance multiplied already-dense GTFS
    // lines several-fold without making them look smoother.
    const steps = Math.max(2, Math.ceil(turn / CURVE_SAMPLE_RADIANS))
    for (let step = 1; step <= steps; step++) {
      pushDistinct(output, cubicBezier(
        before,
        firstControl,
        secondControl,
        after,
        step / steps,
      ))
    }
  }

  pushDistinct(output, simplified[simplified.length - 1])
  return output
}
